import {
  Issue,
  IssueMessageType,
  MediaPickerComponent,
  Media,
  Message,
  Name,
  NewMessage,
  useCreateIssueMessage,
  useNewIssueMessageSubscription,
  useSideChatMessages,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { NativeScrollEvent, Pressable, ScrollView, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { AccountAvatar } from '../accountAvatar';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { MessageThreadComposer } from '../message-thread/MessageThreadComposer';
import { IssueMessage } from './issueMessage';
import { AnonymousId, FunctionId, getIssueFullName } from './issueFormatUtils';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type Participant = { id: string; name: string; avatar?: Media };

export type SideChatPanelProps = {
  /** The issue the side chat lives under (needs `id` + `workspace.id`). */
  issue: Issue;
  /** The main-thread message the side chat hangs off of. */
  originMessage: Message;
  /** Called when the back control is pressed (returns to the conversation). */
  onBack: () => void;
  /** Platform media picker for the composer (web supplies its MediaPicker). */
  mediaPicker: MediaPickerComponent;
  /** Base URL for media assets, forwarded to avatars + message rows. */
  mediaUrl: string;
};

/**
 * SideChatPanel (plan 0022 §C) — ported from gripp-os `SubConversationPanel`,
 * adapted to Crop Circle production primitives.
 *
 * A message-scoped thread rendered as its own SlideLayout column:
 *   - Header: back control + orange "Side Chat" title + read-only overlapping
 *     participant avatars derived client-side from the distinct authors of the
 *     origin + parented messages (no rename / membership editor — out of scope).
 *   - Pinned origin message at the top (read-only reuse of the message row).
 *   - Message list: the SYSTEM_MESSAGE divider renders as a centered gray line;
 *     USER_MESSAGE replies reuse the standard message row. Live replies arrive
 *     via the issue subscription filtered to this origin.
 *   - Composer: reuses MessageThreadComposer; submit parents the new message to
 *     the origin so it routes into this side chat (not the main thread).
 */
export const SideChatPanel = ({
  issue,
  originMessage,
  onBack,
  mediaPicker,
  mediaUrl,
}: SideChatPanelProps) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useSideChatMessages(originMessage.id);
  const { createIssueMessage } = useCreateIssueMessage();
  const [messages, setMessages] = useState<Message[]>([]);

  // Seed (and re-seed on refetch/next-page) from the server. useSideChatMessages
  // orders oldest-first (updatedAt Asc), so the panel reads top-to-bottom, and
  // pages 20 at a time — `data.pages` grows as fetchNextPage (below) loads more.
  // Merge (don't replace): server items in server order, then any local-only
  // messages appended so optimistic own-posts (onSend) and subscription replies
  // aren't wiped when `data` updates before the server round-trips them in.
  // Once the server includes a local message it dedupes back into server order.
  // `data` is loosely typed ({}), so narrow the paged shape before flattening.
  useEffect(() => {
    const paged = data as { pages?: { items?: Message[] }[] } | undefined;
    const serverItems =
      paged?.pages?.flatMap((page) => page?.items ?? []) ?? [];
    setMessages((prev) => {
      const serverIds = new Set(serverItems.map((m) => m.id));
      const localOnly = prev.filter((m) => !serverIds.has(m.id));
      return [...serverItems, ...localOnly];
    });
  }, [data]);

  // Threads over 20 messages truncated on reload because nothing ever called
  // fetchNextPage. Messages render oldest-first top-to-bottom (not inverted),
  // so "load more" here means loading NEWER replies as the user scrolls
  // toward the bottom — mirrors the isCloseToBottom pattern in
  // infiniteDataGrid.tsx, adapted from FlatList's onEndReached since this is a
  // plain ScrollView.
  const onScroll = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }: NativeScrollEvent) => {
    const paddingToBottom = 20;
    const isCloseToBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom;
    if (isCloseToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Live replies. The issue-scoped subscription excludes the current user's own
  // messages (createdBy ne self), so own posts are appended optimistically in
  // onSend instead. Accept only messages parented to THIS origin.
  useNewIssueMessageSubscription(issue.id, (response) => {
    const incoming = response.data.newIssueMessage as Message | undefined;
    if (incoming?.parentMessage?.id === originMessage.id) {
      setMessages((prev) =>
        prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]
      );
    }
    return Promise.resolve();
  });

  const participants = useMemo<Participant[]>(() => {
    const byId = new Map<string, Participant>();
    const add = (audit?: { id?: string; name?: Name; avatar?: Media }) => {
      const id = audit?.id;
      if (!id || id === AnonymousId || id === FunctionId) return;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: getIssueFullName(id, audit?.name ?? undefined),
          avatar: audit?.avatar,
        });
      }
    };
    add(originMessage.updatedBy);
    messages.forEach((m) => add(m.updatedBy));
    return [...byId.values()];
  }, [originMessage, messages]);

  const onSend = async (message: NewMessage) => {
    await createIssueMessage({
      issue,
      message: { ...message, parentMessage: { id: originMessage.id } },
      options: {
        onSettled: (created?: Message) => {
          if (!created) return;
          setMessages((prev) =>
            prev.some((m) => m.id === created.id) ? prev : [...prev, created]
          );
        },
      },
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Pressable
          onPress={onBack}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Back to conversation"
          testID="sidechat-back-button"
          style={styles.backButton}
        >
          <Icon name="Chevron Left" size={20} color={Colors.role.textStrong} />
        </Pressable>
        <View style={styles.headerBody}>
          <Text style={styles.title}>Side Chat</Text>
          {participants.length > 0 && (
            <View style={styles.participantsRow}>
              <View style={styles.avatars}>
                {participants.slice(0, 4).map((p, index) => (
                  <View
                    key={p.id}
                    style={[
                      styles.avatarWrap,
                      index > 0 && styles.avatarOverlap,
                    ]}
                  >
                    <AccountAvatar
                      avatar={p.avatar}
                      mediaUrl={mediaUrl}
                      size={16}
                    />
                  </View>
                ))}
              </View>
              <Text style={styles.participantNames} numberOfLines={1}>
                {participants.map((p) => p.name).join(', ')}
              </Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.pinnedOrigin} testID="sidechat-origin">
        <IssueMessage
          message={originMessage}
          mediaUrl={mediaUrl}
          isUnread={false}
          issue={issue}
          isFirstMessage={false}
        />
      </View>

      <ScrollView
        style={styles.messages}
        contentContainerStyle={styles.messagesContent}
        onScroll={({ nativeEvent }) => onScroll(nativeEvent)}
        scrollEventThrottle={16}
      >
        {messages.map((m) =>
          m.type === IssueMessageType.SystemMessage ? (
            <View
              key={m.id}
              style={styles.divider}
              testID={`sidechat-divider-${m.id}`}
            >
              <Text style={styles.dividerText}>{m.text}</Text>
            </View>
          ) : (
            <IssueMessage
              key={m.id}
              message={m}
              mediaUrl={mediaUrl}
              isUnread={false}
              issue={issue}
              isFirstMessage={false}
            />
          )
        )}
      </ScrollView>

      <MessageThreadComposer
        onPostMessage={onSend}
        mediaPicker={mediaPicker}
        workspaceId={issue.workspace?.id}
        issueId={issue.id}
        placeholder="Chat with context…"
      />
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    flex: 1,
    minHeight: 0,
    backgroundColor: role.canvas,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: role.borderSubtle,
  },
  backButton: {
    marginTop: 2,
  },
  headerBody: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    ...tokens.typography.body.mediumEmphasised,
    color: role.brandOnSurface,
  },
  participantsRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  avatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarWrap: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: role.canvas,
  },
  avatarOverlap: {
    marginLeft: -4,
  },
  participantNames: {
    flex: 1,
    fontSize: 11,
    lineHeight: 14,
    color: role.textSecondary,
  },
  pinnedOrigin: {
    borderBottomWidth: 1,
    borderBottomColor: role.borderSubtle,
  },
  messages: {
    flex: 1,
    minHeight: 0,
  },
  messagesContent: {
    paddingVertical: tokens.spacing.sm,
  },
  divider: {
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    alignItems: 'center',
  },
  dividerText: {
    fontSize: 11,
    lineHeight: 16,
    color: role.textSecondary,
    textAlign: 'center',
  },
}));
